import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  loginApi,
  logoutApi,
  changePasswordApi,
  mapBackendUser,
} from "../api/auth";
import { TOKEN_KEY, REFRESH_TOKEN_KEY, USER_SESSION_KEY } from "../api/authKeys";
import {
  clearAuthStorage,
  getAuthValue,
  hydrateAuthStorage,
  setAuthValue,
} from "../utils/authStorage";
import { startSessionKeepAlive } from "../utils/sessionKeepAlive";
import {
  cleanupPushNotifications,
  initPushNotifications,
  unregisterPushFromBackend,
} from "../services/pushNotifications";
import { resolveEffectiveFrontendRole } from "../utils/hrStaffHelpers";
import AppLoadingScreen from "../componants/common/AppLoadingScreen";

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return context;
}

const SESSION_STORAGE_KEY = USER_SESSION_KEY;

export const DEFAULT_INITIAL_PASSWORD = "Welcome@123!";

function normalizeStoredUser(raw) {
  if (!raw || typeof raw !== "object") return null;
  const role = resolveEffectiveFrontendRole(raw.role, {
    departmentId: raw.departmentId,
    backendRoles: raw.backendRoles || [],
  });
  if (role === raw.role) return raw;
  return { ...raw, role };
}

function clearLocalSession() {
  clearAuthStorage();
}

function readStoredUser() {
  try {
    const saved = getAuthValue(SESSION_STORAGE_KEY);
    const accessToken = getAuthValue(TOKEN_KEY);
    const refreshToken = getAuthValue(REFRESH_TOKEN_KEY);
    if (!saved || !(accessToken || refreshToken)) return null;
    return normalizeStoredUser(JSON.parse(saved));
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => readStoredUser());
  const [loading, setLoading] = useState(() => !readStoredUser());

  // Restore session on cold start from localStorage first so the splash
  // never waits on a native plugin. Capacitor Preferences is a backup only.
  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      try {
        if (!readStoredUser()) {
          await hydrateAuthStorage();
          if (cancelled) return;
          const restored = readStoredUser();
          if (restored) {
            setAuthValue(SESSION_STORAGE_KEY, JSON.stringify(restored));
            setUser(restored);
          }
        } else {
          hydrateAuthStorage().catch(() => {});
        }
      } catch {
        // Keep whatever session we already restored.
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    restoreSession();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (loading || !user) return undefined;
    return startSessionKeepAlive();
  }, [loading, user]);

  // Init push on login AND on cold-start session restore.
  // FCM registration does not need a JWT; backend token sync does.
  useEffect(() => {
    if (loading || !user) return;

    initPushNotifications().catch((err) => {
      console.error("[Push] Init failed:", err?.message || "unknown error");
    });
  }, [loading, user]);

  useEffect(() => {
    function handleTokenRefreshed() {
      if (!user) return;
      initPushNotifications().catch((err) => {
        console.error("[Push] Re-sync after token refresh failed:", err?.message || "unknown error");
      });
    }
    window.addEventListener("auth:token-refreshed", handleTokenRefreshed);
    return () =>
      window.removeEventListener("auth:token-refreshed", handleTokenRefreshed);
  }, [user]);

  useEffect(() => {
    async function handleForcedLogout() {
      try {
        await unregisterPushFromBackend();
      } catch {
        // best effort — JWT may already be cleared
      }
      await cleanupPushNotifications();
      clearLocalSession();
      setUser(null);
    }
    window.addEventListener("auth:logout", handleForcedLogout);
    return () => window.removeEventListener("auth:logout", handleForcedLogout);
  }, []);

  const login = useCallback(async (identifier, password, options = {}) => {
    try {
      const data = await loginApi(identifier.trim(), password.trim(), {
        ...options,
        tenantSlug: options.tenantSlug
          ? String(options.tenantSlug).trim().toLowerCase()
          : undefined,
      });
      if (!data?.user) {
        return { success: false, error: "invalid_credentials" };
      }

      const mustChangePassword = Boolean(
        data.mustChangePassword || data.user?.mustChangePassword,
      );
      const frontendUser = mapBackendUser({
        ...data.user,
        mustChangePassword,
      });
      setAuthValue(SESSION_STORAGE_KEY, JSON.stringify(frontendUser));
      setUser(frontendUser);
      window.dispatchEvent(new CustomEvent("auth:login", { detail: frontendUser }));
      return { success: true, mustChangePassword };
    } catch (error) {
      return { success: false, error };
    }
  }, []);

  const logout = useCallback(async () => {
    // 1) Delete device token while JWT is still valid
    try {
      await unregisterPushFromBackend();
    } catch {
      // best effort — backend logout also purges tokens
    }
    // 2) Remove Capacitor listeners
    await cleanupPushNotifications();
    // 3) Backend logout (also deleteMany DeviceToken) then clear JWT
    try {
      await logoutApi();
    } catch {
      // best effort
    }
    // 4) Clear local session
    clearLocalSession();
    setUser(null);
  }, []);

  const changePassword = useCallback(
    async (currentPassword, newPassword, confirmPassword) => {
      if (!user) {
        return { success: false, error: "يجب تسجيل الدخول أولاً" };
      }
      if (!currentPassword?.trim()) {
        return { success: false, error: "كلمة المرور الحالية مطلوبة" };
      }
      if (!newPassword || newPassword.length < 8) {
        return {
          success: false,
          error: "كلمة المرور يجب أن تكون 8 أحرف على الأقل",
        };
      }
      if (newPassword !== confirmPassword) {
        return { success: false, error: "كلمتا المرور غير متطابقتين" };
      }
      if (currentPassword === newPassword) {
        return {
          success: false,
          error: "كلمة المرور الجديدة يجب أن تكون مختلفة عن الحالية",
        };
      }

      try {
        await changePasswordApi(
          currentPassword.trim(),
          newPassword.trim(),
          confirmPassword.trim(),
        );
        const updatedUser = { ...user, mustChangePassword: false };
        setAuthValue(SESSION_STORAGE_KEY, JSON.stringify(updatedUser));
        setUser(updatedUser);
        return { success: true };
      } catch (err) {
        return {
          success: false,
          error:
            err.response?.data?.message || "حدث خطأ أثناء تغيير كلمة المرور",
        };
      }
    },
    [user],
  );

  const isHrLoginAccount = useCallback(
    ({ id, code }) => {
      if (!user) return false;
      return (
        user.role === "hr" &&
        ((id != null && user.id === id) ||
          (code && user.code?.toUpperCase() === code.toUpperCase()))
      );
    },
    [user],
  );

  const setIsResigned = useCallback((isResigned) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = Boolean(isResigned);
      if (Boolean(prev.isResigned) === next) return prev;
      const updatedUser = { ...prev, isResigned: next };
      setAuthValue(SESSION_STORAGE_KEY, JSON.stringify(updatedUser));
      return updatedUser;
    });
  }, []);

  /** Merge fields into the persisted session user (e.g. refreshed workMode). */
  const patchUser = useCallback((fields) => {
    if (!fields || typeof fields !== "object") return;
    setUser((prev) => {
      if (!prev) return prev;
      let changed = false;
      const updatedUser = { ...prev };
      for (const [key, value] of Object.entries(fields)) {
        if (updatedUser[key] !== value) {
          updatedUser[key] = value;
          changed = true;
        }
      }
      if (!changed) return prev;
      setAuthValue(SESSION_STORAGE_KEY, JSON.stringify(updatedUser));
      return updatedUser;
    });
  }, []);

  if (loading) {
    return <AppLoadingScreen message="جارٍ التحقق من الجلسة..." />;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        login,
        logout,
        changePassword,
        setIsResigned,
        patchUser,
        isHrLoginAccount,
        role: user?.role || null,
        loading,
        isLoading: loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
